import { bounded, officeCall, ANALYSIS_TIMEOUTS } from "../utils/analysis-async";
import { AnalysisRun, AnalysisRunService } from "../services/analysis-run.service";
import { asyncCallback } from "../utils/analysis-async";
import { IAnalyzer } from "../interfaces/analyzer";
import { AnalysisResult } from "../models/analysis-result";
import { ConfigService } from "../services/config.service";
import { RiskLevel } from "../models/risk-level";
import { IAnalyzerConfig } from "../interfaces/analyzer-config";
import { MessageService } from "../services/message.service";
import { MailUtils } from "../utils/mail.utils";
import { HttpClient } from "@angular/common/http";
import { ExchangeRequests } from "../models/exchange.requests";
import { LogService } from "../services/log.service";
import { Inject, Injector } from "@angular/core";
import { ANALYZERS, ATTRIBUTES, RESULT_STATUS, TAGS } from "../models/constants";
import { environment } from "../../environments/environment";
import { SharedService } from "../services/shared.service";
import { TranslateService } from "@ngx-translate/core";

export class CorrespondenceAnalyzer implements IAnalyzer {
    private readonly run: AnalysisRun;
    private info = {};

    private configService: ConfigService
    private log: LogService
    private messageService: MessageService
    private http: HttpClient
    private sharedService: SharedService
    private translate: TranslateService
    constructor(
        @Inject(Injector) private injector: Injector
    ) {
        this.run = this.injector.get(AnalysisRunService).current;
        this.configService = this.injector.get(ConfigService)
        this.log = this.injector.get(LogService)
        this.messageService = this.injector.get(MessageService)
        this.http = this.injector.get(HttpClient)
        this.sharedService = this.injector.get(SharedService)
        this.translate = this.injector.get(TranslateService)
    }

    async runAnalysis(): Promise<AnalysisResult> {
        this.run.assertActive();
        const config = this.configService.getAnalyzerConfiguration(ANALYZERS.CORRESPONDENCE);
        this.info = {};
        const level = !this.messageService.isMobileHost() && !Office.context.requirements.isSetSupported('Mailbox', '1.5')
            ? await this.analyzeCorrespondenceLegacy() : await this.analyzeCorrespondence();
        this.run.assertActive();
        return new AnalysisResult(config.displayName, level, config.weight, config.description!, this.info);
    }

    private async analyzeCorrespondence(): Promise<RiskLevel> {
        return asyncCallback<RiskLevel>(async resolve => {

            let baseUrl = `${environment.apiConfig.microsoft_graph_url_personal}`

            const email = encodeURIComponent(this.run.item.sender.emailAddress);

            const fromQuery: string = `${baseUrl}/messages?$count=true&$search="from:${email}"`;
            const toQuery: string = `${baseUrl}/mailfolders/SentItems/messages?$count=true&$search="to:${email}"`;

            const from: number = await this.getMessageCount(fromQuery);
            const to: number = await this.getMessageCount(toQuery);

            let fromCount = from['count']
            let fromNextUrl = from['next']
            let toCount = to['count']
            let toNextUrl = to['next']

            if (fromNextUrl) {
                await this.getMessageCount(fromNextUrl).then((value) => {
                    fromCount += value['count']
                    fromNextUrl = value['next']
                });
            }

            if (toNextUrl) {
                await this.getMessageCount(toNextUrl).then((value) => {
                    toCount += value['count']
                    toNextUrl = value['next']
                });
            }

            resolve(this.checkCount(toCount, fromCount))
        });
    }

    private checkCount(toCount: number, fromCount: number){
        if (toCount > 0) {
            this.info = {
                ...this.info, fromCount: fromCount, toCount: toCount,
                info: this.translate.instant('analyzer.sent_or_replied_to_sender') }
            return RiskLevel.Low
        } else {
            this.info = {
                ...this.info, fromCount: fromCount, toCount: toCount,
                info: this.translate.instant('analyzer.never_sent_or_replied_to_sender') }
            return RiskLevel.High
        }
    }

    private async analyzeCorrespondenceLegacy(): Promise<RiskLevel> {
        const xml = await officeCall<string>(callback => Office.context.mailbox.makeEwsRequestAsync(
            ExchangeRequests.getFoldersRequest(), callback), 'Correspondence folders', this.run);
        const folders = ExchangeRequests.parseFolderIds(MailUtils.parseXmlResponse(xml));
        if (!folders?.length) return RiskLevel.High;
        const email = encodeURIComponent(this.run.item.sender.emailAddress);
        const from = await this.legacyFolderSearch(`from:${email}`, folders[0], folders);
        const to = await this.legacyFolderSearch(`to:${email}`, folders[0], folders);
        return this.checkCount(to, from);
    }

    private async legacyFolderSearch(query: string, folder: string, folders: string[], index = 0): Promise<number> {
        this.run.assertActive();
        if (!folder) return 0;
        const xml = await officeCall<string>(callback => Office.context.mailbox.makeEwsRequestAsync(
            ExchangeRequests.findItemsRequest(query, folder), callback), 'Correspondence search', this.run);
        const doc = MailUtils.parseXmlResponse(xml);
        const root = doc.getElementsByTagName(TAGS.ROOT_FOLDER)[0] || doc.getElementsByTagName(TAGS.M_ROOT_FOLDER)[0];
        const count = Number(root?.getAttribute(ATTRIBUTES.TOTAL_ITEMS_IN_VIEW) || 0);
        if (count > 1 || index + 1 >= folders.length) return count;
        return count + await this.legacyFolderSearch(query, folders[index + 1], folders, index + 1);
    }

    private async getMessageCount(query: string): Promise<any> {
        this.run.assertActive();
        let subscription: import('rxjs').Subscription;
        try {
            const response: any = await bounded(() => new Promise((resolve, reject) => {
                subscription = this.http.get(query).subscribe({ next: resolve, error: reject });
            }), ANALYSIS_TIMEOUTS.graph, 'Correspondence search', this.run);
            return { count: response.value.length, next: response['@odata.nextLink'] };
        } finally { subscription?.unsubscribe(); }
    }
}
